import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { Loader2, Shirt, Eye, EyeOff, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/auth-context';
import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import type { LoginFormData } from '@/types/schemas';
import { loginSchema } from '@/types/schemas';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

function LoginPage() {
  const { login: handleLogin, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [isVerifyingMfa, setIsVerifyingMfa] = useState(false);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Clear any stale auth data on login page mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthError = urlParams.get('auth') === 'error';
    if (hasAuthError) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  }, []);

  // Redirect to dashboard if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      window.location.href = '/';
    }
  }, [isAuthenticated]);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = form;

  const onSubmit = async (data: LoginFormData) => {
    try {
      const response = await api.auth.login(data);
      
      if (response.mfaRequired) {
        setMfaRequired(true);
        toast({
          title: 'Two-Factor Authentication',
          description: 'Please enter the verification code from your authenticator app.',
        });
        return;
      }

      if (!response.user) {
         throw new Error('Invalid login response');
      }

      handleLogin({
        id: String(response.user.id),
        name: response.user.fullName || response.user.email,
        role: (response.user.role as any) || 'admin',
        email: response.user.email, 
        hasSeenWalkthrough: response.user.hasSeenWalkthrough ?? false,
        mfaEnabled: response.user.mfaEnabled,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      
      toast({
        title: 'Welcome back!',
        description: 'Successfully logged in.',
      });

    } catch (error: any) {
      const message = error.message || 'Invalid email or password';
      if (error.status === 401) {
        setError('email', { type: 'manual', message: ' ' });
        setError('password', { type: 'manual', message: 'Invalid email or password' });
      }
      toast({
        title: 'Login Failed',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const onSubmitMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mfaToken.length !== 6) return;
    
    setIsVerifyingMfa(true);
    try {
      const response = await api.auth.mfaVerifyLogin(mfaToken);
      
      if (!response.user) {
          throw new Error('Verification failed');
      }

      handleLogin({
        id: String(response.user.id),
        name: response.user.fullName || response.user.email,
        role: (response.user.role as any) || 'admin',
        email: response.user.email, 
        hasSeenWalkthrough: response.user.hasSeenWalkthrough ?? false,
        mfaEnabled: response.user.mfaEnabled,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      toast({
        title: 'Identity Verified',
        description: 'Login successful.',
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Verification Failed',
        description: error.message || 'Invalid 2FA code',
      });
    } finally {
      setIsVerifyingMfa(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[conic-gradient(at_top_left,_var(--tw-gradient-stops))] from-slate-900 via-purple-900 to-slate-900">
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          filter: 'contrast(150%) brightness(100%)'
        }}
      />
      
      <Card className="w-full max-w-md border-opacity-20 bg-white/10 backdrop-blur-xl shadow-2xl relative z-10 border-white/20 text-white overflow-hidden">
        {!mfaRequired ? (
          <>
            <CardHeader className="text-center space-y-4 pb-8">
              <div className="flex justify-center">
                <div className="bg-primary/20 p-3 rounded-full ring-1 ring-white/20">
                  <Shirt className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="space-y-2">
                <CardTitle className="text-3xl font-bold tracking-tight">Welcome Back</CardTitle>
                <CardDescription className="text-slate-300">
                  Sign in to manage your laundry business
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-200">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@laundry.com"
                    required
                    disabled={isSubmitting}
                    className="bg-black/20 border-white/10 text-white placeholder:text-slate-400 focus-visible:ring-offset-0 focus-visible:border-white/40 transition-all hover:bg-black/30"
                    {...register('email')}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-400 font-medium" role="alert">{errors.email.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-200">Password</Label>
                    <a href="#" className="text-sm text-purple-300 hover:text-purple-200 transition-colors">
                      Forgot password?
                    </a>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      required
                      disabled={isSubmitting}
                      className="bg-black/20 border-white/10 text-white placeholder:text-slate-400 focus-visible:ring-offset-0 focus-visible:border-white/40 transition-all hover:bg-black/30 pr-10"
                      {...register('password')}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent text-slate-400 hover:text-white"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-red-400 font-medium" role="alert">{errors.password.message}</p>
                  )}
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-white text-slate-900 hover:bg-slate-200 transition-all font-semibold h-11" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Sign In'}
                </Button>
              </form>
            </CardContent>
          </>
        ) : (
          <>
            <CardHeader className="text-center space-y-4 pb-8">
              <div className="flex justify-center">
                <div className="bg-success/20 p-3 rounded-full ring-1 ring-white/20">
                  <ShieldCheck className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="space-y-2">
                <CardTitle className="text-3xl font-bold tracking-tight">Verify Identity</CardTitle>
                <CardDescription className="text-slate-300">
                  Enter the 6-digit code from your app
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmitMfa} className="space-y-6">
                <div className="space-y-4">
                  <Input 
                    autoFocus
                    placeholder="000000"
                    className="bg-black/20 border-white/10 text-white text-center text-3xl h-16 tracking-widest font-mono focus-visible:ring-offset-0 focus-visible:border-white/40"
                    maxLength={6}
                    value={mfaToken}
                    onChange={(e) => setMfaToken(e.target.value.replace(/\D/g, ''))}
                  />
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-white text-slate-900 hover:bg-slate-200 transition-all font-semibold h-11" 
                    disabled={isVerifyingMfa || mfaToken.length !== 6}
                  >
                    {isVerifyingMfa ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Verify & Sign In'}
                  </Button>
                </div>
                
                <Button 
                  type="button" 
                  variant="ghost" 
                  className="w-full text-slate-400 hover:text-white"
                  onClick={() => setMfaRequired(false)}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Login
                </Button>
              </form>
            </CardContent>
          </>
        )}
        <CardFooter className="flex flex-col gap-4 text-center border-t border-white/10 pt-6">
          <p className="text-sm text-slate-400">
            Demo: admin@laundry.com / admin123
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
